// ScoreCardBuilder.ts
// Dynamically generates a table for 18 holes and all game managers.
// For the current transition phase, score entry updates only the raw score
// model and syncs that hole score to Firebase. GameManager scoring is not
// updated locally here; that will later be rebuilt from Firestore snapshots.
import { AccessManager, PlayerStatus, getGameData, setGameData, } from "shared";
import { buildRosterEntries, validateRoundPlayers, makeRoundContext, } from "./RoundContextBuilder";
import { GameManagerRegistry } from "./GameManagers";
import { FourBall_GameManager } from "./GameManagers/FourBall_GameManager";
import { AvRuntimeManager } from "./AvRuntimeManager";
import { HoleScoreEditor } from "./HoleScoreEditor";
export class ScoreCardBuilder {
    constructor(stored_scoreCard, repo, devPatch) {
        // All game managers for this scorecard
        this.gms = [];
        // Map each manager to its 18 hole cells (index = holeIndex 0..17)
        this.cellFor = new Map();
        this.headerCellFor = new Map();
        this.holesByIndex = [];
        this.inputsByIndex = [];
        this.hasAppliedInitialSnapshot = false;
        this.stored_scoreCard = stored_scoreCard;
        this.repo = repo;
        this.devPatch = devPatch;
        this.element = document.createElement("div");
        this.holeScoreEditor = new HoleScoreEditor({
            storedScoreCard: this.stored_scoreCard,
            getRoster: () => this.roundContext.roster,
            parseGrossByRosterIndex: (rawScore) => this.parseGrossByRosterIndex(rawScore),
            isPlayerWithdrawn: (playerId) => this.isPlayerWithdrawn(playerId),
            isPlayerCheckedIn: (playerId) => this.isPlayerCheckedIn(playerId),
            isPlayerActiveForScoring: (player) => this.isPlayerActiveForScoring(player),
            persistRawHoleScore: (hole, newRaw, holeIndex) => this.persistRawHoleScore(hole, newRaw, holeIndex),
        });
    }
    // ---------------------------------------------------------------------------
    // Public API
    // ---------------------------------------------------------------------------
    buildPlayerScores() {
        return this.roundContext.roster.map((player, rosterIndex) => {
            const gross = [];
            for (let holeIndex = 0; holeIndex < this.roundContext.holesInRound; holeIndex++) {
                const raw = this.holesByIndex[holeIndex]?.score ?? "";
                const grossByRosterIndex = this.parseGrossByRosterIndex(raw);
                gross[holeIndex] = this.isPlayerActiveForScoring(player)
                    ? grossByRosterIndex[rosterIndex] ?? null
                    : null;
            }
            return {
                playerId: player.id,
                gross,
            };
        });
    }
    async persistRawHoleScore(hole, newRaw, holeIndex) {
        const access = AccessManager.fromBrowserStorage(this.stored_scoreCard);
        if (!access.canEditScores()) {
            console.warn('[persistRawHoleScore] Score edit blocked because this device is not the current scorekeeper', {
                currentPlayerInitial: access.currentPlayerId,
                scorekeeperInitial: access.scorekeeperId,
                holeIndex,
            });
            return;
        }
        if (newRaw === hole.score)
            return;
        // 1. Update runtime model
        hole.setScore(newRaw);
        // 2. Keep the full-scorecard display input in sync when present.
        const input = this.inputsByIndex[holeIndex];
        if (input) {
            input.value = newRaw;
        }
        // 3. Update persisted scorecard model
        const storedHole = this.stored_scoreCard.stored_hole?.[holeIndex];
        if (!storedHole) {
            console.warn("[persistRawHoleScore] Missing stored_hole entry", {
                holeIndex,
                holeNum: hole.holeNum,
                newRaw,
            });
            return;
        }
        storedHole.score = newRaw;
        this.stored_scoreCard.lastRawScoreUpdate = {
            updatedBy: "",
            holeIndex,
            rawScore: newRaw,
        };
        try {
            await this.repo.ScoreCards.update(this.stored_scoreCard);
        }
        catch (err) {
            console.error("[persistRawHoleScore] repo.update failed", {
                cardId: this.stored_scoreCard.id,
                holeIndex,
                newRaw,
                err,
            });
            return;
        }
    }
    buildScoreCard(group, course) {
        const scope = "ScorecardBuilder.buildScoreCard";
        const { totalHoles, holes, table, thead } = this.initBasicsAndDom(course, scope);
        const { roundContext, gameManagers } = this.initRoundContextAndManagers(group, holes, totalHoles, scope);
        this.roundContext = roundContext;
        this.gms = gameManagers;
        this.buildHeaderRow(table, thead, roundContext);
        const tbody = this.buildBodyRows(holes, scope);
        table.appendChild(tbody);
        this.refreshAllHoleScores();
        this.element.appendChild(table);
    }
    // ---------------------------------------------------------------------------
    // Phase 1: basics & DOM shell
    // ---------------------------------------------------------------------------
    initBasicsAndDom(course, scope) {
        let result;
        const totalHoles = this.stored_scoreCard?.stored_hole?.length ?? course?.holesInRound ?? 18;
        const holes = Array.isArray(this.stored_scoreCard?.stored_hole)
            ? this.stored_scoreCard.stored_hole
            : [];
        // Reset DOM + internal state
        this.element.innerHTML = "";
        this.gms = [];
        this.cellFor.clear();
        this.headerCellFor.clear();
        this.holesByIndex = [];
        this.inputsByIndex = [];
        const cardHeader = document.createElement("div");
        cardHeader.innerHTML = `
        <h2>${course.id}</h2>
        <p>Tee Time: ${this.formatTeeTime(this.stored_scoreCard.date)} | Scorekeeper: ${this.stored_scoreCard.scoreKeeper}</p>
      `;
        this.element.appendChild(cardHeader);
        const table = document.createElement("table");
        table.classList.add("scorecard");
        const thead = document.createElement("thead");
        result = { totalHoles, holes, table, thead };
        return result;
    }
    formatTeeTime(value) {
        if (!value)
            return "";
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) {
            return value;
        }
        return date.toLocaleString([], {
            month: "numeric",
            day: "numeric",
            year: "numeric",
            hour: "numeric",
            minute: "2-digit",
        });
    }
    // ---------------------------------------------------------------------------
    // Phase 2: round context & managers
    // ---------------------------------------------------------------------------
    validateScorecardPlayerHandicaps() {
        for (const player of this.stored_scoreCard.players ?? []) {
            if (!Number.isFinite(Number(player.handicap))) {
                throw new Error(`Scorecard player ${player.displayCode} is missing a valid handicap.`);
            }
        }
    }
    initRoundContextAndManagers(group, holes, totalHoles, scope) {
        let result;
        const gameNames = this.resolveGameNames();
        const playersArg = Array.isArray(this.stored_scoreCard.players)
            ? this.stored_scoreCard.players
                .map(player => String(player.displayCode ?? "").trim())
                .filter(displayCode => displayCode.length > 0)
                .join(",")
            : String(this.stored_scoreCard.players ?? "");
        this.validateScorecardPlayerHandicaps();
        const players = this.stored_scoreCard.players.map(player => ({
            memberId: player.memberId,
            displayCode: player.displayCode,
            handicap: Number(player.handicap),
        }));
        const rosterEntries = buildRosterEntries(players, group);
        const validationError = validateRoundPlayers(rosterEntries);
        if (validationError) {
            throw new Error(validationError);
        }
        const roundContext = makeRoundContext({
            holesInRound: totalHoles,
            roster: rosterEntries,
            pars: holes.map((h) => Number(h?.par ?? 4)),
            handicaps: holes.map((h) => Number(h?.handicap ?? h?.hcp ?? 0)),
        });
        const gameManagers = this.createGameManagers(gameNames, roundContext);
        this.bindFourBallTeamSaving(gameManagers);
        result = { roundContext, gameManagers };
        return result;
    }
    applyStrokePatternClass(cell, gm, holeIndex) {
        cell.classList.remove("sp_0", "sp_1", "sp_2", "sp_01", "sp_10", "sp_11", "sp_12", "sp_21", "sp_22");
        const patternProvider = gm;
        const patternClass = patternProvider.getStrokePatternClassForHole?.(holeIndex);
        if (patternClass) {
            cell.classList.add(patternClass);
            return;
        }
        const strokes = gm.getStrokesOnHole ? gm.getStrokesOnHole(holeIndex) : 0;
        if (strokes >= 1) {
            cell.classList.add("sp_1");
            if (strokes >= 2) {
                cell.classList.add("sp_2");
            }
        }
    }
    refreshStrokePatternClasses(gm) {
        const cells = this.cellFor.get(gm);
        if (!cells)
            return;
        for (let holeIndex = 0; holeIndex < cells.length; holeIndex++) {
            const cell = cells[holeIndex];
            if (!cell)
                continue;
            this.applyStrokePatternClass(cell, gm, holeIndex);
            this.applyGameScoreCellRender(cell, gm, holeIndex);
        }
    }
    bindFourBallTeamSaving(gameManagers) {
        for (const gm of gameManagers) {
            if (!(gm instanceof FourBall_GameManager))
                continue;
            gm.onTeamsChanged(async (teams) => {
                const handicapSetup = teams.handicapMode === "usga"
                    ? {
                        mode: "playerHandicaps",
                        playerHandicapPercent: teams.handicapPercent ?? 100,
                    }
                    : {
                        mode: "teamHandicaps",
                        team1Handicap: teams.team1Handicap ?? 0,
                        team2Handicap: teams.team2Handicap ?? 0,
                    };
                const fourBallData = {
                    teams: {
                        team1: [teams.team1Player1, teams.team1Player2],
                        team2: [teams.team2Player1, teams.team2Player2],
                    },
                    header: teams.header,
                    handicapSetup,
                };
                setGameData(this.stored_scoreCard, gm.name, fourBallData);
                gm.createHandicapManager(fourBallData);
                gm.createScoringManager(fourBallData);
                this.refreshStrokePatternClasses(gm);
                try {
                    await this.repo.ScoreCards.update(this.stored_scoreCard);
                }
                catch (err) {
                    console.error("[bindFourBallTeamSaving] repo.update failed", {
                        cardId: this.stored_scoreCard.id,
                        teams,
                        err,
                    });
                }
            });
        }
    }
    resolveGameNames() {
        if (Array.isArray(this.stored_scoreCard.selectedGames)) {
            return this.stored_scoreCard.selectedGames;
        }
        return String(this.stored_scoreCard.selectedGames ?? "")
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean);
    }
    getGameHeaderLines(gm) {
        const score = gm.getScore("scorecard-game-header", undefined, this.playerScores, this.getCurrentPlayerId());
        return Array.isArray(score) ? score : [score, ""];
    }
    getCurrentPlayerId() {
        return AccessManager.fromBrowserStorage(this.stored_scoreCard).currentPlayerId ?? undefined;
    }
    createGameManagers(gameNames, rc) {
        if (!rc) {
            throw new Error("createGameManagers called with undefined roundContext");
        }
        const managers = [];
        for (const name of gameNames) {
            const key = name.toLowerCase().trim();
            const ctor = GameManagerRegistry[key];
            if (!ctor) {
                console.warn(`Game manager for "${name}" not found in registry.`);
                continue;
            }
            const created = ctor.CreateGameManagers(rc, this.stored_scoreCard, () => this.stored_scoreCard);
            managers.push(...created);
        }
        return managers;
    }
    // ---------------------------------------------------------------------------
    // Phase 2c: header row
    // ---------------------------------------------------------------------------
    buildHeaderRow(table, thead, roundContext) {
        const tr = document.createElement("tr");
        const scoresHeader = roundContext.roster.map((r) => r.displayCode).join("");
        const addTh = (text) => {
            const th = document.createElement("th");
            th.textContent = text;
            tr.appendChild(th);
            return th;
        };
        addTh("Hole");
        addTh("Par");
        addTh("Hcp");
        addTh(scoresHeader || "Score");
        for (const gm of this.gms) {
            const th = document.createElement("th");
            th.classList.add("gm-header");
            const headerElementId = gm.getHeaderElementId?.();
            if (headerElementId) {
                th.id = headerElementId;
            }
            const headerCssClasses = gm.getHeaderCssClasses?.();
            if (headerCssClasses?.length) {
                th.classList.add(...headerCssClasses);
            }
            const headerTitle = gm.getHeaderTitle?.();
            if (headerTitle) {
                th.title = headerTitle;
            }
            const clickHandler = gm.getHeaderClickHandler?.();
            if (clickHandler) {
                th.style.cursor = "pointer";
                th.addEventListener("click", clickHandler);
            }
            const [existingTitle, existingScore] = this.getGameHeaderLines(gm);
            const headerRender = gm.getHeaderRender?.();
            if (headerRender?.cssClass) {
                th.classList.add(...headerRender.cssClass.split(" ").filter(Boolean));
            }
            const titleDiv = document.createElement("div");
            titleDiv.className = "gm-header-title";
            const scoreDiv = document.createElement("div");
            scoreDiv.className = "gm-header-score";
            if (headerRender?.html) {
                titleDiv.innerHTML = headerRender.html;
                scoreDiv.textContent = "";
            }
            else {
                titleDiv.textContent = headerRender?.text ?? existingTitle;
                scoreDiv.textContent = existingScore;
            }
            th.appendChild(titleDiv);
            th.appendChild(scoreDiv);
            tr.appendChild(th);
            this.headerCellFor.set(gm, th);
        }
        thead.innerHTML = "";
        thead.appendChild(tr);
        table.appendChild(thead);
    }
    // ---------------------------------------------------------------------------
    // Phase 3: body rows, listeners, shading
    // ---------------------------------------------------------------------------
    buildBodyRows(holes, scope) {
        const tbody = document.createElement("tbody");
        let previousLayoutName;
        holes.forEach((holeData, holeIndex) => {
            // If the layout has changed (or this is the first hole), insert a header row
            if (holeData.layoutName && holeData.layoutName !== previousLayoutName) {
                const headerRow = this.buildLayoutHeaderRow(holeData.layoutName);
                tbody.appendChild(headerRow);
                previousLayoutName = holeData.layoutName;
            }
            const row = this.buildHoleRow(holeData, holeIndex, scope);
            tbody.appendChild(row);
        });
        return tbody;
    }
    buildLayoutHeaderRow(layoutName) {
        const tr = document.createElement("tr");
        tr.classList.add("layout-header-row");
        const td = document.createElement("td");
        const fixedColumns = 4; // Hole, Par, Hcp, Score
        const gameColumns = this.gms.length;
        td.colSpan = fixedColumns + gameColumns;
        td.textContent = layoutName;
        tr.appendChild(td);
        return tr;
    }
    findPlayerStatus(playerId) {
        const normalizedPlayerId = String(playerId ?? "").trim().toUpperCase();
        const statuses = Array.isArray(this.stored_scoreCard.playerStatuses)
            ? this.stored_scoreCard.playerStatuses
            : [];
        return statuses.find(s => String(s.memberId ?? "").trim().toUpperCase() === normalizedPlayerId ||
            String(s.displayCode ?? "").trim().toUpperCase() === normalizedPlayerId);
    }
    isPlayerCheckedIn(playerId) {
        const status = this.findPlayerStatus(playerId);
        return status?.status === "self" || status?.status === "proxy";
    }
    isPlayerWithdrawn(playerId) {
        const status = this.findPlayerStatus(playerId);
        return PlayerStatus.from(status?.status).isWithdrawn();
    }
    isPlayerActiveForScoring(player) {
        if (!player)
            return false;
        return (this.isPlayerCheckedIn(player.id) &&
            !this.isPlayerWithdrawn(player.id));
    }
    parseGrossByRosterIndex(rawScore) {
        const playerCount = this.roundContext.roster.length;
        const raw = (rawScore ?? "").trim();
        if (!raw) {
            return new Array(playerCount).fill(null);
        }
        // Case 1: comma or space separated, e.g. "4,5,3,4" or "4 5 3 4"
        if (raw.includes(",") || raw.includes(" ")) {
            const parts = raw.split(/[,\s]+/).filter(Boolean);
            const result = [];
            for (let i = 0; i < playerCount; i++) {
                const token = parts[i];
                if (!token) {
                    result.push(null);
                    continue;
                }
                const n = Number(token);
                result.push(Number.isFinite(n) ? n : null);
            }
            return result;
        }
        // Case 2: compact digits, e.g. "4534"
        const result = [];
        for (let i = 0; i < playerCount; i++) {
            const ch = raw[i];
            if (!ch) {
                result.push(null);
                continue;
            }
            const n = Number(ch);
            result.push(Number.isFinite(n) && n > 0 ? n : null);
        }
        return result;
    }
    buildHoleRow(holeData, holeIndex, scope) {
        const row = document.createElement("tr");
        const holeCell = document.createElement("td");
        holeCell.textContent = String(holeData.holeNum);
        row.appendChild(holeCell);
        const parCell = document.createElement("td");
        parCell.textContent = String(holeData.par ?? "");
        row.appendChild(parCell);
        const hcpCell = document.createElement("td");
        hcpCell.textContent = String(holeData.handicap ?? holeData?.hcp ?? "");
        row.appendChild(hcpCell);
        const hole = this.hydrateHole(holeData);
        this.holesByIndex[holeIndex] = hole;
        const inputCell = document.createElement("td");
        inputCell.classList.add("score-entry-cell");
        const input = document.createElement("input");
        input.type = "text";
        input.readOnly = true;
        input.placeholder = "";
        input.value = hole.score;
        input.classList.add("score-entry-display");
        this.inputsByIndex[holeIndex] = input;
        inputCell.appendChild(input);
        row.appendChild(inputCell);
        inputCell.addEventListener("click", () => {
            this.openHoleScoreEditor(holeIndex);
        });
        for (const gm of this.gms) {
            const resultCell = document.createElement("td");
            resultCell.classList.add("scorecell");
            this.applyStrokePatternClass(resultCell, gm, holeIndex);
            this.applyGameScoreCellRender(resultCell, gm, holeIndex);
            row.appendChild(resultCell);
            let cells = this.cellFor.get(gm);
            if (!cells) {
                cells = [];
                this.cellFor.set(gm, cells);
            }
            cells[holeIndex] = resultCell;
        }
        hole.onScoreChange(() => {
            try {
                this.refreshAllHoleScores();
                this.buildIndexPageScoreSummary();
            }
            finally {
            }
        });
        return row;
    }
    getCurrentPlayerIndex() {
        const currentPlayerId = this.getCurrentPlayerId();
        if (!currentPlayerId)
            return -1;
        return this.stored_scoreCard.players.findIndex(p => p.memberId === currentPlayerId);
    }
    applyGameScoreCellRender(cell, gm, holeIndex) {
        // Remove render classes applied during the previous render.
        // This fixes stale FourBall winner dots when a won hole becomes tied,
        // blank, withdrawn, or otherwise no longer won.
        const previousCssClasses = cell.dataset.gameScoreCellRenderCssClasses;
        if (previousCssClasses) {
            cell.classList.remove(...previousCssClasses.split(" ").filter(Boolean));
            delete cell.dataset.gameScoreCellRenderCssClasses;
        }
        const playerIndex = this.getCurrentPlayerIndex();
        const cellRender = gm.getScoreCellRender?.(holeIndex, playerIndex, this.playerScores);
        if (cellRender?.cssClass) {
            const cssClasses = cellRender.cssClass.split(" ").filter(Boolean);
            if (cssClasses.length > 0) {
                cell.classList.add(...cssClasses);
                cell.dataset.gameScoreCellRenderCssClasses = cssClasses.join(" ");
            }
        }
        cell.title = cellRender?.title ?? "";
        if (cellRender?.html) {
            cell.innerHTML = cellRender.html;
        }
    }
    async toggleMatchPlayHandicaps() {
        const access = AccessManager.fromBrowserStorage(this.stored_scoreCard);
        if (!access.canEditScores()) {
            console.warn("[toggleMatchPlayHandicaps] Change blocked because this device is not the current scorekeeper", { cardId: this.stored_scoreCard.id });
            return;
        }
        const current = getGameData(this.stored_scoreCard, "Match Play");
        setGameData(this.stored_scoreCard, "Match Play", { useHandicaps: !(current.useHandicaps ?? false) });
        try {
            await this.repo.ScoreCards.update(this.stored_scoreCard);
        }
        catch (err) {
            console.error("[toggleMatchPlayHandicaps] repo.update failed", {
                cardId: this.stored_scoreCard.id,
                err,
            });
        }
    }
    getLastCompletedHoleIndex() {
        return this.getLastScoredHoleIndex();
    }
    getHoleCount() {
        return this.roundContext.holesInRound;
    }
    canEditScores() {
        return AccessManager.fromBrowserStorage(this.stored_scoreCard).canEditScores();
    }
    isHoleScored(holeIndex) {
        if (holeIndex < 0 || holeIndex >= this.roundContext.holesInRound)
            return false;
        const grossByRosterIndex = this.parseGrossByRosterIndex(this.holesByIndex[holeIndex]?.score ?? "");
        const activeIndexes = this.roundContext.roster
            .map((player, rosterIndex) => ({ player, rosterIndex }))
            .filter(({ player }) => this.isPlayerActiveForScoring(player))
            .map(({ rosterIndex }) => rosterIndex);
        return activeIndexes.length > 0 && activeIndexes.every((rosterIndex) => grossByRosterIndex[rosterIndex] !== null);
    }
    setLocalHoleScoreSavedHandler(handler) {
        this.localHoleScoreSavedHandler = handler;
    }
    async openHoleScoreEditor(holeIndex) {
        if (!this.canEditScores())
            return null;
        const hole = this.holesByIndex[holeIndex];
        if (!hole) {
            console.warn("[ScoreCardBuilder] Cannot edit missing hole", { holeIndex });
            return null;
        }
        const result = await this.holeScoreEditor.open(hole, holeIndex);
        if (result.status === "saved") {
            this.localHoleScoreSavedHandler?.({
                editedHoleIndex: result.editedHoleIndex,
                lastCompletedHoleIndex: this.getLastCompletedHoleIndex(),
            });
        }
        return result;
    }
    async buildIndexPageScoreSummary(requestedHoleIndex) {
        this.refreshAllHoleScores();
        const holeIndex = requestedHoleIndex ?? this.getLastScoredHoleIndex();
        if (holeIndex < 0) {
            return "No scores entered";
        }
        try {
            const rawGameSummaries = this.buildGameSummaryContracts(holeIndex);
            const selectedGames = this.resolveGameNames();
            if (this.devPatch) {
                await this.devPatch.sendSelectedGames(selectedGames);
                return await this.devPatch.processRawGameSummaries(rawGameSummaries);
            }
            await AvRuntimeManager.initialize(selectedGames);
            return await AvRuntimeManager.processRawGameSummaries(rawGameSummaries);
        }
        catch (err) {
            console.error("[ScoreCardBuilder] buildIndexPageScoreSummary failed", {
                cardId: this.stored_scoreCard.id,
                holeIndex,
                err,
            });
            return "Unable to build score summary";
        }
    }
    updateGameHeadersWithCurrentScore(gm) {
        const headerCell = this.headerCellFor.get(gm);
        if (!headerCell)
            return;
        const titleDiv = headerCell.querySelector(".gm-header-title");
        const scoreDiv = headerCell.querySelector(".gm-header-score");
        const [existingTitle, existingScore] = this.getGameHeaderLines(gm);
        const headerRender = gm.getHeaderRender?.();
        if (headerRender?.cssClass) {
            headerCell.classList.add(...headerRender.cssClass.split(" ").filter(Boolean));
        }
        if (titleDiv) {
            if (headerRender?.html) {
                titleDiv.innerHTML = headerRender.html;
            }
            else {
                titleDiv.textContent = headerRender?.text ?? existingTitle;
            }
        }
        if (scoreDiv) {
            scoreDiv.textContent = headerRender?.html ? "" : existingScore;
        }
    }
    UpdateGameCellForHole(gm, holeIndex) {
        const displayValue = gm.getScore("scorecard-game-cell", holeIndex, this.playerScores, this.getCurrentPlayerId());
        const cells = this.cellFor.get(gm);
        const cell = cells?.[holeIndex];
        if (cell) {
            cell.textContent = Array.isArray(displayValue)
                ? displayValue.join(" ")
                : displayValue;
            this.applyStrokePatternClass(cell, gm, holeIndex);
            this.applyGameScoreCellRender(cell, gm, holeIndex);
        }
    }
    refreshAllHoleScores() {
        this.playerScores = this.buildPlayerScores();
        for (const gm of this.gms) {
            for (let holeIndex = 0; holeIndex < this.roundContext.holesInRound; holeIndex++) {
                this.UpdateGameCellForHole(gm, holeIndex);
            }
            this.updateGameHeadersWithCurrentScore(gm);
        }
    }
    // ---------------------------------------------------------------------------
    // Hole model
    // ---------------------------------------------------------------------------
    // Converts raw IHoleData into a live IHole with listeners
    hydrateHole(data) {
        const listeners = [];
        // Coerce initial score: treat "0" / undefined as blank
        const initial = (data.score ?? "").trim();
        const startScore = initial === "0" ? "" : initial;
        const hole = {
            holeNum: data.holeNum,
            par: data.par,
            handicap: data.handicap ?? data.hcp ?? 0,
            score: startScore,
            setScore(newScore) {
                if (newScore === hole.score)
                    return;
                hole.score = newScore;
                for (const fn of listeners)
                    fn(newScore);
            },
            setScoreSilently(newScore) {
                hole.score = newScore;
            },
            resetScore() {
                hole.score = "";
            },
            onScoreChange(cb) {
                listeners.push(cb);
                return () => {
                    const i = listeners.indexOf(cb);
                    if (i >= 0)
                        listeners.splice(i, 1);
                };
            },
        };
        return hole;
    }
    async applyScorecardSnapshot(scorecard) {
        try {
            this.stored_scoreCard = scorecard;
            const storedHoles = scorecard.stored_hole ?? [];
            if (this.holesByIndex.length === 0) {
                console.warn("No hydrated holes are available yet.");
                return;
            }
            for (let holeIndex = 0; holeIndex < this.holesByIndex.length; holeIndex++) {
                const liveHole = this.holesByIndex[holeIndex];
                const storedHole = storedHoles[holeIndex];
                if (!liveHole || !storedHole)
                    continue;
                const raw = (storedHole.score ?? "").trim();
                const snapshotScore = raw === "0" ? "" : raw;
                const input = this.inputsByIndex[holeIndex];
                if (input) {
                    input.value = snapshotScore;
                }
                liveHole.resetScore();
                liveHole.setScoreSilently(snapshotScore);
            }
            this.refreshAllHoleScores();
        }
        catch (err) {
            console.error("applyScorecardSnapshot failed:", err);
        }
    }
    isCurrentDeviceScorekeeper(scorecard) {
        return AccessManager.fromBrowserStorage(scorecard).isScorekeeper;
    }
    buildGameSummaryContracts(holeIndex) {
        const result = {};
        const gameNames = this.resolveGameNames();
        const uniqueGameNames = gameNames.filter((gameName, index, all) => all.findIndex(name => name.toLowerCase().trim() ===
            gameName.toLowerCase().trim()) === index);
        for (const gameName of uniqueGameNames) {
            const key = gameName.toLowerCase().trim();
            const ctor = GameManagerRegistry[key];
            if (!ctor) {
                console.warn(`Game manager for "${gameName}" not found in registry.`);
                continue;
            }
            const provider = ctor;
            if (typeof provider.getAvGameSummary !== "function") {
                console.log(`Game manager "${gameName}" does not yet provide an AV game summary.`);
                continue;
            }
            const managers = this.getManagersOfType(ctor).filter(manager => {
                const playerIds = manager.players;
                // GameSummary should contain only games whose participants are
                // currently active in the round. A player is active only when
                // checked in as self/proxy and not withdrawn.
                return (playerIds ?? []).every(playerId => this.isPlayerCheckedIn(playerId) &&
                    !this.isPlayerWithdrawn(playerId));
            });
            const contribution = provider.getAvGameSummary(managers, holeIndex, this.playerScores);
            Object.assign(result, contribution);
        }
        return result;
    }
    getManagersOfType(ctor) {
        return this.gms.filter((gm) => gm instanceof ctor);
    }
    buildScorecardLink(scorecard) {
        return `https://leaderboardnow.com`;
    }
    getLastScoredHoleIndex() {
        for (let i = this.holesByIndex.length - 1; i >= 0; i--) {
            const score = (this.holesByIndex[i]?.score ?? "").trim();
            if (score && score !== "0") {
                return i;
            }
        }
        return -1;
    }
    isCurrentPlayerGame(gm) {
        const currentPlayerId = this.getCurrentPlayerId();
        if (!currentPlayerId)
            return false;
        return gm.includesPlayer(currentPlayerId);
    }
}
//# sourceMappingURL=ScorecardBuilder.js.map